import { Paper, Tabs, Tab, Typography, Box } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { AuthDialog } from "app-constants/constants"
import { createPath, createURL_SSR, ROUTE } from "app-constants/routing"
import { ActivityIndicator } from "components/activity-indicator"
import { ContentContainer } from "components/content-container"
import { ItineraryEvents } from "components/itineraries/events"
import MainLayout from "components/main-layout"
import { ItineraryMeetings } from "components/meetings/ScheduledMeetings"
import useAuthRedirect from "hooks/useAuthRedirect"
import useHeadingStyles from "hooks/useHeadingStyles"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import applyCache from "lib/mw/apply-cache"
import applyCurrentServerContext from "lib/mw/apply-current-server-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyTitle from "lib/mw/apply-title"
import useCurrent, { Current } from "lib/use-current"
import ConventionRegistration from "models/convention-api/v1/ConventionRegistration"
import { useRouter } from "next/router"
import { switcher } from "rambdax"
import { useState, useEffect } from "react"
import tryToGetAppLogo from "utils/tryToGetAppLogo"

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentServerContext(),
  applyTitle(({ props, query }) => {
    if (query.activeTab === "1") {
      return "My Meetings"
    } else {
      return `My ${props.current.config.expo_copy_text}`
    }
  }),
  applyNextSeo((context) => ({
    openGraph: {
      title: "My Schedule",
      url: createURL_SSR(context, { path: ROUTE.ITINERARIES }),
      type: "website",
      images: [tryToGetAppLogo(context.props.current)],
    },
  })),
)

enum ItineraryTabsIndexes {
  MY_EVENTS = 0,
  MY_MEETINGS = 1,
}

const useStyles = makeStyles()({
  style1: {
    fontFamily: "Lato",
    fontWeight: "bold",
  },
  style2: {
    fontFamily: "Lato",
    fontWeight: "normal",
    color: "#556064",
  },
})

interface ICurrentActiveTab {
  activeTabIndex: ItineraryTabsIndexes
  conventions: ConventionRegistration[]
}

const CurrentActiveTab = (props: ICurrentActiveTab) => {
  // prettier-ignore
  return switcher<any>(props.activeTabIndex)
      .is(ItineraryTabsIndexes.MY_EVENTS, <ItineraryEvents {...props} />)
      .is(ItineraryTabsIndexes.MY_MEETINGS,  <ItineraryMeetings {...props} />)
      .default(null)
}

interface ItinerariesPageProps {
  current: Current
}

// @ts-ignore
export default buildServerSidePage(function ItinerariesPage(props: ItinerariesPageProps) {
  const current = useCurrent()

  const router = useRouter()

  const { classes } = useStyles()

  const activeTab = Number(router.query?.activeTab) || 0

  const [activeTabIndex, setActiveTabIndex] = useState<ItineraryTabsIndexes>(activeTab)

  const onTabChange = (event: any, newValue: any) => {
    event.preventDefault()
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        activeTab: newValue,
      },
    })
  }

  useEffect(() => {
    setActiveTabIndex(activeTab)
  }, [activeTab])

  useEffect(() => {
    // Avoid redirect loop when this and useAuthRedirect both fire
    // @ts-ignore
    if (!current?.user) {
      return
    }

    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        activeTab,
      },
    })
    // @ts-ignore
  }, [current, current?.user])

  // @ts-ignore
  const { config: providerConfig } = useCurrent()
  const { classes: headingClasses } = useHeadingStyles()

  useAuthRedirect({
    defaultRoute: createPath({ path: ROUTE.CONVENTIONS }),
    defaultDialog: { dialog: AuthDialog.JOIN, payload: { redirectUrl: createPath({ path: ROUTE.ITINERARIES }), preamble: '', dialogHeader: 'Sign In' } },
  })()

  const { data: conventions } = ConventionRegistration.useAll(() => {
    if (!current?.user) return null

    return {
      includes: ["convention"],
    }
  })

  if (!providerConfig.expo_copy_text || !conventions) return null

  return (
    <MainLayout layout="macro">
      <ContentContainer alignItems="left">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="20px"
          maxWidth={["100%"]}
          pt={[3, 3, 5]}>
          <Typography variant="h1" className={headingClasses.h1}>
            My Schedule
          </Typography>
        </Box>
        <Paper square elevation={0}>
          <Tabs
            value={activeTabIndex}
            indicatorColor="primary"
            textColor="primary"
            onChange={onTabChange}
            aria-label="Account Navigation">
            <Tab
              className={activeTab == 0 ? classes.style1 : classes.style2}
              label={`My ${providerConfig.expo_copy_text}`}
            />
            <Tab className={activeTab == 1 ? classes.style1 : classes.style2} label="My meetings" />
          </Tabs>
        </Paper>
        {/*
        // @ts-ignore */}
        {!current.user ? (
          <Box pt={[4]}>
            <ActivityIndicator />
          </Box>
        ) : (
          <CurrentActiveTab activeTabIndex={activeTabIndex} conventions={conventions} />
        )}
      </ContentContainer>
    </MainLayout>
  )
})
